import {
  forwardRef, useMemo,
} from 'react';
import { createColor } from '~/_shared/components/colorPicker/colorPicker.helpers';
import type { ColorResult } from '~/_shared/components/colorPicker/colorPicker.types';
import {
  labelStyle, textOnlyStyle, wrapperStyle,
} from '~/_shared/components/labelVisualizer/labelVisualizer.styles';
import {
  BARELY_READABLE_CONTRAST, getContrastColor, isContrastSufficient, mixColorMemoized,
} from '~/_shared/utils/colors/colors.helpers';
import { type QuadrilateralDimensions } from '../../types/coordinateSystem/coordinateSystem';
import {
  initialLabelVisualizerArrowProps, initialLabelVisualizerBodyProps, initialLabelVisualizerBorderProps,
} from './labelVisualizer.constants';
import {
  type LabelVisualizerArrowProps, type LabelVisualizerBodyProps, type LabelVisualizerBorderProps,
} from './labelVisualizer.types';

type LabelVisualizerProps = Readonly<{
  arrowProps?: Partial<LabelVisualizerArrowProps>;
  bodyProps?: Partial<LabelVisualizerBodyProps>;
  borderProps?: Partial<LabelVisualizerBorderProps>;
  label?: string;
  maxDimensions?: QuadrilateralDimensions;
  underlayColor: string;
}>;

export const LabelVisualizerComponent = forwardRef<HTMLDivElement, LabelVisualizerProps>(({
  arrowProps: arrowOptions,
  bodyProps: bodyOptions,
  borderProps: borderOptions,
  label,
  maxDimensions,
  underlayColor,
  ...restProps
}, ref) => {
  const initialBodyProps = initialLabelVisualizerBodyProps;
  const initialArrowProps = initialLabelVisualizerArrowProps;
  const initialBorderProps = initialLabelVisualizerBorderProps;

  const updatedBodyProps: LabelVisualizerBodyProps = useMemo(() => ({
    ...initialBodyProps,
    ...bodyOptions,
    padding: {
      ...initialBodyProps.padding,
      ...(bodyOptions && bodyOptions.padding),
    },
    dimensions: {
      ...initialBodyProps.dimensions,
      ...(bodyOptions && bodyOptions.dimensions),
    },
  }), [initialBodyProps, bodyOptions]);

  const updatedArrowProps: LabelVisualizerArrowProps = useMemo(() => ({
    ...initialArrowProps,
    ...arrowOptions,
    dimensions: {
      ...initialArrowProps.dimensions,
      ...(arrowOptions && arrowOptions.dimensions),
    },
  }), [initialArrowProps, arrowOptions]);

  const updatedBorderProps: LabelVisualizerBorderProps = useMemo(() => ({
    ...initialBorderProps,
    ...borderOptions,
  }), [initialBorderProps, borderOptions]);

  const contrastColors = useMemo(() => {
    const {
      backgroundColor: { opacity: fillOpacity, selectedColor: fillColor },
      fontColor: { selectedColor: fontColor, opacity: fontOpacity },
    } = updatedBodyProps;
    const { selectedColor: borderColor, opacity: borderOpacity } = updatedBorderProps.color;

    const compositeFillColor = mixColorMemoized(underlayColor, fillColor, fillOpacity ?? 1).toHexString();
    const compositeBorderColor = mixColorMemoized(underlayColor, borderColor, borderOpacity ?? 1).toHexString();
    const compositeFontColor = mixColorMemoized(underlayColor, fontColor, fontOpacity ?? 1).toHexString();

    const options = { tolerance: BARELY_READABLE_CONTRAST };

    const isFillColorContrasting = isContrastSufficient(compositeFillColor, underlayColor, options.tolerance);
    const isBorderContrasting = isContrastSufficient(compositeBorderColor, underlayColor, options.tolerance);
    const isTextContrasting = isContrastSufficient(compositeFontColor, underlayColor, options.tolerance);

    const contrastColors: {
      fontContrastColor?: ColorResult;
      borderContrastColor?: ColorResult;
    } = {
      fontContrastColor: undefined,
      borderContrastColor: undefined,
    };

    if (!isFillColorContrasting && !isTextContrasting) {
      contrastColors.fontContrastColor = getContrastColor(createColor(compositeFontColor), createColor(compositeFillColor), options);
    }

    if (!isFillColorContrasting && !isBorderContrasting) {
      contrastColors.borderContrastColor = getContrastColor(createColor(compositeBorderColor), createColor(underlayColor), options);
    }

    return contrastColors;
  }, [updatedBodyProps, updatedBorderProps?.color, underlayColor]);

  return (
    <div
      {...restProps}
      css={wrapperStyle}
      ref={ref}
    >
      <div
        css={labelStyle(
          { bodyProps: updatedBodyProps, arrowProps: updatedArrowProps,
            borderProps: updatedBorderProps, maxDimensions }, underlayColor, contrastColors.borderContrastColor?.hex)
        }
      >
        {label}
      </div>
      {label && (
        <div
          css={textOnlyStyle(
            { bodyProps: updatedBodyProps, arrowProps: updatedArrowProps,
              borderProps: updatedBorderProps, maxDimensions }, contrastColors.fontContrastColor?.hex)}
        >
          {label}
        </div>
      )}
    </div>
  );
});

LabelVisualizerComponent.displayName = 'LabelVisualizerComponent';
